import { z } from "zod";
import { displayValue } from "../utils/format.js";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const getRunActionRepetitionsInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID)"),
  runId: z.string().describe("The run ID (GUID)"),
  actionName: z
    .string()
    .describe("The exact action name inside the for_each/do_until loop"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
  failedOnly: z
    .boolean()
    .optional()
    .describe("If true, only return failed iterations (default: false)"),
  includeInputs: z
    .boolean()
    .optional()
    .describe("If true, fetch input data for each iteration (default: false)"),
  includeOutputs: z
    .boolean()
    .optional()
    .describe("If true, fetch output data for each iteration (default: true for failed)"),
  maxIterations: z
    .number()
    .optional()
    .describe("Maximum iterations to return (default: 50). Use to limit large loops."),
});

export type GetRunActionRepetitionsInput = z.infer<typeof getRunActionRepetitionsInputSchema>;

export const getRunActionRepetitionsTool = {
  name: "get_run_action_repetitions",
  description:
    "Get iteration-level details for a for_each or do_until loop action in a flow run. Shows which iterations succeeded/failed, their inputs/outputs, and error details. Essential for debugging loops that partially fail.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID (GUID)",
      },
      runId: {
        type: "string",
        description: "The run ID (GUID)",
      },
      actionName: {
        type: "string",
        description:
          "The exact action name inside the for_each/do_until loop",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
      failedOnly: {
        type: "boolean",
        description: "If true, only return failed iterations (default: false)",
      },
      includeInputs: {
        type: "boolean",
        description:
          "If true, fetch input data for each iteration (default: false)",
      },
      includeOutputs: {
        type: "boolean",
        description:
          "If true, fetch output data for each iteration (default: true for failed)",
      },
      maxIterations: {
        type: "number",
        description:
          "Maximum iterations to return (default: 50). Use to limit large loops.",
      },
    },
    required: ["flowId", "runId", "actionName"],
  },
};

const TRUNCATE_LIMIT = 1000;

function formatData(data: unknown): string {
  if (data && typeof data === "object" && "body" in data) {
    const body = (data).body;
    const str = typeof body === "object" && body !== null ? JSON.stringify(body, null, 2) : displayValue(body);
    return str.length > TRUNCATE_LIMIT ? str.slice(0, TRUNCATE_LIMIT) + "…" : str;
  }
  const str = JSON.stringify(data, null, 2);
  return str.length > TRUNCATE_LIMIT ? str.slice(0, TRUNCATE_LIMIT) + "…" : str;
}

export async function handleGetRunActionRepetitions(
  api: FlowManagementApi,
  input: unknown
): Promise<{ text: string; structured?: Record<string, unknown> }> {
  const parsed = getRunActionRepetitionsInputSchema.parse(input);
  const maxIter = parsed.maxIterations ?? 50;

  logger.info({ input: parsed }, "Executing get_run_action_repetitions");

  const repetitions = await api.getFlowRunActionRepetitions(
    parsed.flowId,
    parsed.runId,
    parsed.actionName,
    parsed.environment
  );

  let filtered = repetitions;

  if (parsed.failedOnly) {
    filtered = filtered.filter(
      (r) =>
        r.properties.status === "Failed" ||
        r.properties.status === "TimedOut"
    );
  }

  const totalCount = filtered.length;
  filtered = filtered.slice(0, maxIter);

  const shouldFetchInputs = (_status: string): boolean => {
    if (parsed.includeInputs === true) return true;
    return false;
  };

  const shouldFetchOutputs = (status: string): boolean => {
    if (parsed.includeOutputs === true) return true;
    if (parsed.includeOutputs === false) return false;
    return status === "Failed" || status === "TimedOut";
  };

  const iterIO: Record<string, { inputs?: string; outputs?: string }> = {};
  const fetchPromises: Promise<void>[] = [];

  for (const rep of filtered) {
    const status = rep.properties.status;
    const key = rep.name;

    if (shouldFetchInputs(status) && rep.properties.inputsLink?.uri) {
      fetchPromises.push(
        api.fetchResourceLink(rep.properties.inputsLink.uri).then(
          (data) => {
            if (!iterIO[key]) iterIO[key] = {};
            iterIO[key].inputs = formatData(data);
          },
          (err) => {
            if (!iterIO[key]) iterIO[key] = {};
            const msg = err instanceof Error ? err.message : String(err);
            iterIO[key].inputs = `(fetch error: ${msg.slice(0, 100)})`;
          }
        )
      );
    }

    if (shouldFetchOutputs(status) && rep.properties.outputsLink?.uri) {
      fetchPromises.push(
        api.fetchResourceLink(rep.properties.outputsLink.uri).then(
          (data) => {
            if (!iterIO[key]) iterIO[key] = {};
            iterIO[key].outputs = formatData(data);
          },
          (err) => {
            if (!iterIO[key]) iterIO[key] = {};
            const msg = err instanceof Error ? err.message : String(err);
            iterIO[key].outputs = `(fetch error: ${msg.slice(0, 100)})`;
          }
        )
      );
    }
  }

  await Promise.all(fetchPromises);

  const failedCount = filtered.filter(
    (r) => r.properties.status === "Failed" || r.properties.status === "TimedOut"
  ).length;
  const succeededCount = filtered.filter(
    (r) => r.properties.status === "Succeeded"
  ).length;

  const lines: string[] = [
    `Repetitions for "${parsed.actionName}": ${totalCount} total (${succeededCount} succeeded, ${failedCount} failed)`,
  ];

  if (totalCount > maxIter) {
    lines.push(`  (showing first ${maxIter} of ${totalCount})`);
  }

  for (const rep of filtered) {
    const props = rep.properties;
    const startTime = new Date(props.startTime);
    const endTime = props.endTime ? new Date(props.endTime) : undefined;
    const durationMs = endTime ? endTime.getTime() - startTime.getTime() : undefined;
    const duration = durationMs ? `${(durationMs / 1000).toFixed(1)}s` : "";

    const indexes = props.repetitionIndexes
      ?.map((ri) => `${ri.scopeName}[${ri.itemIndex}]`)
      .join(" > ");

    const status =
      props.status === "Succeeded"
        ? "✓"
        : props.status === "Failed"
          ? "✗"
          : props.status === "TimedOut"
            ? "⏱"
            : "○";

    let line = `\n  ${status} ${indexes ?? rep.name} ${duration}`;

    if (props.error) {
      line += `\n    Error: [${props.error.code}] ${props.error.message.slice(0, 500)}`;
    }

    const io = iterIO[rep.name];
    if (io?.inputs) {
      line += `\n    Inputs: ${io.inputs}`;
    }
    if (io?.outputs) {
      line += `\n    Outputs: ${io.outputs}`;
    }

    lines.push(line);
  }

  // Machine-parseable mirror of the prose, emitted as MCP structuredContent (#11).
  const structured: Record<string, unknown> = {
    actionName: parsed.actionName,
    count: totalCount,
    returned: filtered.length,
    succeeded: succeededCount,
    failed: failedCount,
    repetitions: filtered.map((rep) => {
      const props = rep.properties;
      const startMs = new Date(props.startTime).getTime();
      const endMs = props.endTime ? new Date(props.endTime).getTime() : null;
      const io = iterIO[rep.name];

      return {
        name: rep.name,
        ...(props.repetitionIndexes
          ? {
              indexes: props.repetitionIndexes.map((ri) => ({
                scopeName: ri.scopeName,
                itemIndex: ri.itemIndex,
              })),
            }
          : {}),
        status: props.status,
        startTime: props.startTime,
        endTime: props.endTime ?? null,
        durationMs: endMs !== null ? endMs - startMs : null,
        error: props.error ?? null,
        ...(io?.inputs ? { inputs: io.inputs } : {}),
        ...(io?.outputs ? { outputs: io.outputs } : {}),
      };
    }),
  };

  return { text: lines.join("\n"), structured };
}
